import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, type BrandFacts, type FactsContent, type FactsRevision } from '../api';
import { t } from '../i18n';
import { fmtShort } from '../lib/format';
import { Chip, ErrorBox, errorMessage, Field, Spinner, useToast } from './ui';
import '../styles/features.css';

/**
 * The brand's business facts: the client's own knowledge the agent plans with. Each save is a new revision; it counts only once a
 * person approves it. The agent never sees a draft.
 */
type Row = { id: string; text: string; source: string; effectiveAt: string; expiresAt: string };
type Form = Omit<FactsContent, 'protectedUnknowns' | 'facts'> & { protectedUnknowns: string; facts: Row[] };

/** Identical to EDITORIAL_PROTECTED in the api (services/facts.ts): words the agent cannot use in goal or call to action. */
const EDITORIAL_PROTECTED = /\b(capacity|seats?|slots?|packages?|availability|menus?|certification|prices?|rates?|locations?|opening hours)\b/i;
const protectedHint = (text: string) => {
  const m = EDITORIAL_PROTECTED.exec(text);
  return m ? t('settings.facts.protectedWordHint', { word: m[1]!.toLowerCase() }) : undefined;
};

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
const toForm = (c: FactsContent | null, defaults: string[]): Form => ({
  businessName: c?.businessName ?? '', category: c?.category ?? '', goal: c?.goal ?? '', audience: c?.audience ?? '', voice: c?.voice ?? '', cta: c?.cta ?? '',
  protectedUnknowns: (c?.protectedUnknowns ?? defaults).join(', '),
  facts: (c?.facts ?? []).map((f) => ({ id: f.id, text: f.text, source: f.source, effectiveAt: day(f.effectiveAt), expiresAt: day(f.expiresAt) })),
});
const toContent = (f: Form): FactsContent => ({
  businessName: f.businessName, category: f.category, goal: f.goal, audience: f.audience, voice: f.voice, cta: f.cta,
  protectedUnknowns: [...new Set(f.protectedUnknowns.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))],
  facts: f.facts.map((r) => ({
    id: r.id.trim(), text: r.text, source: r.source,
    effectiveAt: new Date(`${r.effectiveAt}T00:00:00Z`).toISOString(),
    expiresAt: r.expiresAt ? new Date(`${r.expiresAt}T00:00:00Z`).toISOString() : null,
  })),
});

export function BusinessFacts({ brandId }: { brandId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, error } = useQuery({ queryKey: ['facts', brandId], queryFn: () => api.get<BrandFacts>(`/api/brands/${brandId}/facts`) });
  const [showHistory, setShowHistory] = useState(false);
  const history = useQuery({ queryKey: ['facts-history', brandId], queryFn: () => api.get<FactsRevision[]>(`/api/brands/${brandId}/facts/revisions`), enabled: showHistory });
  const [form, setForm] = useState<Form | null>(null);
  useEffect(() => { if (data && !form) setForm(toForm(data.latest?.content ?? null, data.default_protected_unknowns ?? [])); }, [data, form]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['facts', brandId] }); qc.invalidateQueries({ queryKey: ['facts-history', brandId] }); };

  const save = useMutation({
    // With published facts, the manual lists are ignored by the server, so none are sent.
    mutationFn: (f: Form) => api.post<FactsRevision>(`/api/brands/${brandId}/facts`, data?.published ? { ...toContent(f), facts: [], protectedUnknowns: [] } : toContent(f)),
    onSuccess: refresh,
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const approve = useMutation({
    mutationFn: (id: string) => api.post<FactsRevision>(`/api/facts/${id}/approve`),
    onSuccess: () => { refresh(); toast(t('settings.facts.approvedToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const published = data?.published ?? null;
  const draft = data?.draft_pending ? data.latest : null;
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const setRow = (i: number, patch: Partial<Row>) => setForm((f) => (f ? { ...f, facts: f.facts.map((r, j) => (j === i ? { ...r, ...patch } : r)) } : f));

  return (
    <>
      <section className="card set-card" aria-labelledby="set-facts">
        <header className="set-card-top">
          <h3 id="set-facts">{t('settings.facts.title')}</h3>
          <p className="muted small">{t('settings.facts.intro')}</p>
        </header>
        {error && <ErrorBox error={error} />}
        {!data && !error && <Spinner />}
        {data && (data.approved
          ? <p className="small"><Chip state="approved" label={t('settings.facts.isApproved')} /> {t('settings.facts.approved', { n: data.approved.number, who: data.approved.approved_by ?? '', when: data.approved.approved_at ? fmtShort(data.approved.approved_at) : '' })}</p>
          : <p className="small muted">{t('settings.facts.none')}</p>)}
        {draft && (
          <div className="row fx-facts-draft" role="status">
            <span className="small">{draft.by_agent ? t('settings.facts.agentDraft', { n: draft.number }) : t('settings.facts.savedDraft', { n: draft.number })}</span>
            <button type="button" className="btn btn-small btn-primary" disabled={approve.isPending} onClick={() => approve.mutate(draft.id)}>{t('settings.facts.approve', { n: draft.number })}</button>
          </div>
        )}
      </section>

      {published && (
        <section className="card set-card" aria-labelledby="set-facts-published">
          <header className="set-card-top">
            <h3 id="set-facts-published">{t('settings.facts.published.title')}</h3>
            <p className="muted small">{t('settings.facts.published.explain')}</p>
          </header>
          <p className="small">
            <a href={published.url} target="_blank" rel="noreferrer">{published.url}</a>{' · '}
            <code>{published.revision.slice(0, 12)}</code>{' · '}
            {t('settings.facts.published.synced', { when: fmtShort(published.observed_at) })}{' · '}
            {t('settings.facts.published.count', { n: published.count })}
          </p>
          <details>
            <summary>{t('settings.facts.published.list')}</summary>
            <ul className="ent-list">
              {published.facts.map((f) => (
                <li key={f.id} className="small">
                  <code>{f.id}</code> {f.text} <span className="muted">({f.source})</span>
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}

      {form && (
        <form className="card set-card stack" onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} aria-labelledby="set-facts-form">
          <header className="set-card-top"><h3 id="set-facts-form">{t('settings.facts.formTitle')}</h3></header>
          <div className="set-fields">
            <Field label={t('settings.facts.businessName')}><input type="text" required maxLength={120} value={form.businessName} onChange={(e) => set({ businessName: e.target.value })} /></Field>
            <Field label={t('settings.facts.category')}><input type="text" required maxLength={60} value={form.category} onChange={(e) => set({ category: e.target.value })} /></Field>
          </div>
          <Field label={t('settings.facts.goal')} hint={protectedHint(form.goal)}><textarea required maxLength={300} value={form.goal} onChange={(e) => set({ goal: e.target.value })} /></Field>
          <Field label={t('settings.facts.audience')}><textarea required maxLength={300} value={form.audience} onChange={(e) => set({ audience: e.target.value })} /></Field>
          <Field label={t('settings.facts.voice')}><textarea required maxLength={300} value={form.voice} onChange={(e) => set({ voice: e.target.value })} /></Field>
          <Field label={t('settings.facts.cta')} hint={protectedHint(form.cta)}><input type="text" required maxLength={200} value={form.cta} onChange={(e) => set({ cta: e.target.value })} /></Field>
          {published ? (
            <div className="stack">
              <h4>{t('settings.facts.protectedUnknowns')}</h4>
              <p className="small muted">{t('settings.facts.published.protectedFromSite')}</p>
              <p className="row">{published.protectedUnknowns.length
                ? published.protectedUnknowns.map((u) => <span key={u} className="chip">{u}</span>)
                : <span className="small muted">{t('settings.facts.published.noProtected')}</span>}</p>
            </div>
          ) : (
            <>
          <Field label={t('settings.facts.protectedUnknowns')} hint={t('settings.facts.protectedHint')}>
            <input type="text" value={form.protectedUnknowns} onChange={(e) => set({ protectedUnknowns: e.target.value })} />
          </Field>
          <h4>{t('settings.facts.facts')}</h4>
          <ul className="ent-list fx-facts-list">
            {form.facts.map((r, i) => (
              <li key={i} className="fx-facts-row">
                <Field label={t('settings.facts.id')}><input type="text" required pattern="[a-z0-9-]{1,40}" value={r.id} onChange={(e) => setRow(i, { id: e.target.value })} /></Field>
                <Field label={t('settings.facts.text')}><input type="text" required maxLength={300} value={r.text} onChange={(e) => setRow(i, { text: e.target.value })} /></Field>
                <Field label={t('settings.facts.source')}><input type="text" required maxLength={200} value={r.source} onChange={(e) => setRow(i, { source: e.target.value })} /></Field>
                <Field label={t('settings.facts.effectiveAt')}><input type="date" required value={r.effectiveAt} onChange={(e) => setRow(i, { effectiveAt: e.target.value })} /></Field>
                <Field label={t('settings.facts.expiresAt')}><input type="date" value={r.expiresAt} onChange={(e) => setRow(i, { expiresAt: e.target.value })} /></Field>
                <button type="button" className="btn btn-small btn-ghost" onClick={() => set({ facts: form.facts.filter((_, j) => j !== i) })}>{t('settings.facts.removeFact')}</button>
              </li>
            ))}
          </ul>
          <div className="row">
            <button type="button" className="btn btn-small" disabled={form.facts.length >= 60} onClick={() => set({ facts: [...form.facts, { id: '', text: '', source: '', effectiveAt: new Date().toISOString().slice(0, 10), expiresAt: '' }] })}>{t('settings.facts.addFact')}</button>
          </div>
            </>
          )}
          <div className="row">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? t('settings.facts.saving') : t('settings.facts.save')}</button>
          </div>
        </form>
      )}

      <section className="card set-card">
        <details onToggle={(e) => setShowHistory((e.target as HTMLDetailsElement).open)}>
          <summary>{t('settings.facts.history')}</summary>
          {history.error && <ErrorBox error={history.error} />}
          {showHistory && !history.data && !history.error && <Spinner />}
          <ul className="ent-list">
            {(history.data ?? []).map((r) => (
              <li key={r.id} className="small">
                {t('settings.facts.historyRow', { n: r.number, who: r.author ?? t('settings.facts.agent'), when: fmtShort(r.created_at) })}{' '}
                {r.approved_at ? <Chip state="approved" label={t('settings.facts.isApproved')} /> : <span className="muted">{t('settings.facts.isDraft')}</span>}
              </li>
            ))}
          </ul>
        </details>
      </section>
    </>
  );
}
